// src/services/frontend-bundler/trading.service.ts
import { 
  Connection, 
  PublicKey, 
  Keypair,
  Transaction,
  VersionedTransaction,
  LAMPORTS_PER_SOL,
  ComputeBudgetProgram
} from '@solana/web3.js';
import axios from 'axios';

interface JupiterQuote {
  inputMint: string;
  outputMint: string;
  inAmount: string;
  outAmount: string;
  priceImpactPct: number;
  marketInfos: any[];
  slippageBps: number;
  platformFee?: any;
  routePlan?: any[];
}

interface SwapResult {
  success: boolean;
  signature?: string;
  error?: string;
  inputAmount?: number;
  outputAmount?: number;
}

// ✅ Add proper type definitions for Solana transaction errors
interface SolanaTransactionError {
  InstructionError?: [number, any];
  [key: string]: any;
}

interface TransactionConfirmation {
  confirmed: boolean;
  error?: string;
  txStatus?: any;
}

export class TradingService {
  private connection: Connection;
  
  private readonly JUPITER_API_URL = 'https://quote-api.jup.ag/v6';
  private readonly SOL_MINT = 'So11111111111111111111111111111111111111112';
  private readonly USDC_MINT = 'EPjFWdd5AufqSSqeM2qN1xzybapC8G4wEGGkZwyTDt1v';

  constructor(connection?: Connection) {
    // Use InstantNodes RPC with optimized settings for speed
    const rpcUrl = process.env.REACT_APP_SOLANA_RPC_URL || 
      'https://mainnet.helius-rpc.com/?api-key=37327d2d-9742-4fc4-b0d0-57d489b78697';
    
    this.connection = connection || new Connection(rpcUrl, {
      commitment: 'processed', // Use 'processed' for faster responses
      confirmTransactionInitialTimeout: 30000,
      disableRetryOnRateLimit: false,
      httpHeaders: {
        'Cache-Control': 'no-cache'
      }
    });
  }

  async getQuote(
    inputMint: string,
    outputMint: string,
    amount: number,
    slippageBps: number = 100
  ): Promise<JupiterQuote | null> {
    try {
      const params = new URLSearchParams({
        inputMint,
        outputMint,
        amount: amount.toString(),
        slippageBps: slippageBps.toString(),
        swapMode: 'ExactIn',
        onlyDirectRoutes: 'false',
        asLegacyTransaction: 'false',
        maxAccounts: '64',
        minimizeSlippage: 'true',
        autoSlippage: 'true',
        dynamicComputeUnitLimit: 'true',
      });

      const response = await axios.get(`${this.JUPITER_API_URL}/quote?${params}`, {
        timeout: 10000,
        headers: {
          'Accept': 'application/json'
        }
      });
      
      if (response.data) {
        return response.data;
      }
      
      return null;
    } catch (error: any) {
      console.error('Failed to get Jupiter quote:', error.response?.data || error.message);
      return null;
    }
  }

  async swapTokens(
    keypair: Keypair,
    outputMint: string,
    amountInSol: number,
    slippageBps: number = 100
  ): Promise<SwapResult> {
    try {
      const amountInLamports = Math.floor(amountInSol * LAMPORTS_PER_SOL);
      
      // Get quote for SOL -> Token
      const quote = await this.getQuote(
        this.SOL_MINT,
        outputMint,
        amountInLamports,
        slippageBps
      );

      if (!quote) {
        return {
          success: false,
          error: 'Failed to get swap quote'
        };
      }

      console.log(`Quote received: ${amountInSol} SOL -> ${quote.outAmount} tokens`);

      // Get swap transaction from Jupiter with optimized settings
      const swapResponse = await axios.post(
        `${this.JUPITER_API_URL}/swap`,
        {
          quoteResponse: quote,
          userPublicKey: keypair.publicKey.toString(),
          wrapAndUnwrapSol: true,
          dynamicComputeUnitLimit: true,
          prioritizationFeeLamports: 5000,
          asLegacyTransaction: false,
          maxAccounts: 64
        },
        {
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json'
          },
          timeout: 10000
        }
      );

      if (!swapResponse.data || !swapResponse.data.swapTransaction) {
        return {
          success: false,
          error: 'Failed to get swap transaction'
        };
      }

      // Deserialize and sign transaction
      const swapTransactionBuf = Buffer.from(swapResponse.data.swapTransaction, 'base64');
      const transaction = VersionedTransaction.deserialize(swapTransactionBuf);
      transaction.sign([keypair]);

      // Send transaction with proper settings
      const rawTransaction = transaction.serialize();
      const signature = await this.connection.sendRawTransaction(rawTransaction, {
        skipPreflight: false,  // ✅ Enable preflight to catch errors early
        preflightCommitment: 'processed',
        maxRetries: 2
      });

      console.log('Buy transaction sent:', signature);

      // ✅ PROPER CONFIRMATION WITH ERROR CHECKING
      const confirmationResult = await this.confirmTransactionWithTimeout(
        signature, 
        30000 // 30 second timeout
      );

      if (!confirmationResult.confirmed) {
        return {
          success: false,
          error: confirmationResult.error || 'Transaction confirmation failed or timed out',
          signature // Include signature for debugging
        };
      }

      // ✅ CHECK FOR TRANSACTION ERRORS
      if (confirmationResult.error) {
        return {
          success: false,
          error: `Transaction failed on-chain: ${confirmationResult.error}`,
          signature
        };
      }

      // Get token decimals for output calculation
      const decimals = await this.getTokenDecimals(outputMint).catch(() => 9);

      console.log(`✅ Transaction confirmed successfully: ${signature}`);

      return {
        success: true,
        signature,
        inputAmount: amountInSol,
        outputAmount: parseFloat(quote.outAmount) / Math.pow(10, decimals)
      };

    } catch (error: any) {
      console.error('Swap error:', error.message);
      return {
        success: false,
        error: error.message || 'Swap failed'
      };
    }
  }

  private async confirmTransactionWithTimeout(
    signature: string,
    timeoutMs: number = 30000
  ): Promise<TransactionConfirmation> {
    try {
      // Get the latest blockhash for confirmation
      const latestBlockhash = await this.connection.getLatestBlockhash('finalized');
      
      // Create confirmation promise
      const confirmationPromise = this.connection.confirmTransaction({
        signature,
        blockhash: latestBlockhash.blockhash,
        lastValidBlockHeight: latestBlockhash.lastValidBlockHeight
      }, 'confirmed');

      // Create timeout promise
      const timeoutPromise = new Promise<never>((_, reject) =>
        setTimeout(() => reject(new Error('Transaction confirmation timeout')), timeoutMs)
      );

      // Race between confirmation and timeout
      const confirmation = await Promise.race([
        confirmationPromise,
        timeoutPromise
      ]);

      // ✅ CHECK FOR TRANSACTION ERRORS with proper type handling
      if (confirmation.value.err) {
        let errorMessage = 'Unknown transaction error';
        
        // ✅ Type-safe error handling
        const error = confirmation.value.err as any;
        
        if (typeof error === 'string') {
          errorMessage = error;
        } else if (error && typeof error === 'object') {
          // Handle InstructionError
          if ('InstructionError' in error && Array.isArray(error.InstructionError)) {
            const [instructionIndex, customError] = error.InstructionError;
            
            // Parse common Jupiter/DEX errors
            if (customError && typeof customError === 'object' && 'Custom' in customError) {
              const errorCode = customError.Custom;
              errorMessage = this.parseCustomError(errorCode);
            } else {
              errorMessage = `Instruction ${instructionIndex} failed: ${JSON.stringify(customError)}`;
            }
          } else {
            // Handle other error types
            errorMessage = JSON.stringify(error);
          }
        }

        return {
          confirmed: false,
          error: errorMessage,
          txStatus: confirmation.value
        };
      }

      // Transaction succeeded
      return {
        confirmed: true,
        txStatus: confirmation.value
      };

    } catch (error: any) {
      if (error.message.includes('timeout')) {
        // Check if transaction actually succeeded despite timeout
        try {
          const status = await this.connection.getSignatureStatus(signature);
          if (status?.value?.confirmationStatus === 'confirmed' || 
              status?.value?.confirmationStatus === 'finalized') {
            
            if (status.value.err) {
              return {
                confirmed: false,
                error: `Transaction failed: ${JSON.stringify(status.value.err)}`
              };
            }
            
            return { confirmed: true };
          }
        } catch (statusError) {
          console.error('Failed to check transaction status:', statusError);
        }
        
        return {
          confirmed: false,
          error: 'Transaction confirmation timeout - status unknown'
        };
      }

      return {
        confirmed: false,
        error: error.message || 'Confirmation failed'
      };
    }
  }

  // ✅ Helper method to parse common DEX error codes
  private parseCustomError(errorCode: number): string {
    const commonErrors: { [key: number]: string } = {
      0: 'Insufficient funds',
      1: 'Slippage tolerance exceeded', // This is likely your "custom program error: 1"
      2: 'Invalid token account',
      3: 'Token account not found',
      4: 'Invalid mint',
      5: 'Math overflow',
      6: 'Price impact too high',
      12: 'Insufficient SOL for fees',
      // Add more error codes as you encounter them
    };

    return commonErrors[errorCode] || `Custom program error: ${errorCode}`;
  }

  async sellTokens(
    keypair: Keypair,
    tokenMint: string,
    tokenAmount: number,
    slippageBps: number = 100
  ): Promise<SwapResult> {
    try {
      // Get token decimals with fallback
      const decimals = await this.getTokenDecimals(tokenMint).catch(() => 9);
      const amountInSmallestUnit = Math.floor(tokenAmount * Math.pow(10, decimals));
      
      // Get quote for Token -> SOL
      const quote = await this.getQuote(
        tokenMint,
        this.SOL_MINT,
        amountInSmallestUnit,
        slippageBps
      );

      if (!quote) {
        return {
          success: false,
          error: 'Failed to get sell quote'
        };
      }

      console.log(`Sell quote: ${tokenAmount} tokens -> ${parseFloat(quote.outAmount) / LAMPORTS_PER_SOL} SOL`);

      // Get swap transaction with optimized settings
      const swapResponse = await axios.post(
        `${this.JUPITER_API_URL}/swap`,
        {
          quoteResponse: quote,
          userPublicKey: keypair.publicKey.toString(),
          wrapAndUnwrapSol: true,
          dynamicComputeUnitLimit: true,
          prioritizationFeeLamports: 'auto',
          asLegacyTransaction: false,
          maxAccounts: 64
        },
        {
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json'
          },
          timeout: 10000
        }
      );

      if (!swapResponse.data || !swapResponse.data.swapTransaction) {
        return {
          success: false,
          error: 'Failed to get sell transaction'
        };
      }

      // Deserialize and sign
      const swapTransactionBuf = Buffer.from(swapResponse.data.swapTransaction, 'base64');
      const transaction = VersionedTransaction.deserialize(swapTransactionBuf);
      transaction.sign([keypair]);

      // Send with proper settings
      const rawTransaction = transaction.serialize();
      const signature = await this.connection.sendRawTransaction(rawTransaction, {
        skipPreflight: false,  // ✅ Enable preflight
        preflightCommitment: 'processed',
        maxRetries: 2
      });

      console.log('Sell transaction sent:', signature);

      // ✅ PROPER CONFIRMATION WITH ERROR CHECKING
      const confirmationResult = await this.confirmTransactionWithTimeout(signature, 30000);

      if (!confirmationResult.confirmed) {
        return {
          success: false,
          error: confirmationResult.error || 'Transaction confirmation failed',
          signature
        };
      }

      if (confirmationResult.error) {
        return {
          success: false,
          error: `Transaction failed on-chain: ${confirmationResult.error}`,
          signature
        };
      }

      console.log(`✅ Sell transaction confirmed successfully: ${signature}`);

      return {
        success: true,
        signature,
        inputAmount: tokenAmount,
        outputAmount: parseFloat(quote.outAmount) / LAMPORTS_PER_SOL
      };

    } catch (error: any) {
      console.error('Sell error:', error.message);
      return {
        success: false,
        error: error.message || 'Sell failed'
      };
    }
  }

  /**
   * Quick confirmation helper - doesn't throw on timeout
   */
  private async quickConfirm(signature: string): Promise<boolean> {
    try {
      const latestBlockhash = await this.connection.getLatestBlockhash('processed');
      
      // Use processed commitment for speed
      await this.connection.confirmTransaction({
        signature,
        blockhash: latestBlockhash.blockhash,
        lastValidBlockHeight: latestBlockhash.lastValidBlockHeight
      }, 'processed');
      
      return true;
    } catch (error) {
      // Check status even if confirmation fails
      try {
        const status = await this.connection.getSignatureStatus(signature);
        return status?.value !== null && status?.value?.err === null;
      } catch {
        return false;
      }
    }
  }

  /**
   * Get token balance for a wallet
   */
  async getTokenBalance(
    walletAddress: string,
    tokenMint: string
  ): Promise<number> {
    try {
      const wallet = new PublicKey(walletAddress);
      const mint = new PublicKey(tokenMint);
      
      // Get token accounts
      const response = await this.connection.getParsedTokenAccountsByOwner(
        wallet,
        { mint },
        'processed' // Use processed for speed
      );

      if (response.value.length === 0) {
        return 0;
      }

      const balance = response.value[0].account.data.parsed.info.tokenAmount.uiAmount;
      return balance || 0;
    } catch (error) {
      console.error('Failed to get token balance:', error);
      return 0;
    }
  }

  /**
   * Get token decimals with caching
   */
  private tokenDecimalsCache = new Map<string, number>();
  
  private async getTokenDecimals(tokenMint: string): Promise<number> {
    // Check cache first
    if (this.tokenDecimalsCache.has(tokenMint)) {
      return this.tokenDecimalsCache.get(tokenMint)!;
    }

    // Known token decimals (hardcoded for speed)
    const knownDecimals: { [key: string]: number } = {
      'So11111111111111111111111111111111111111112': 9, // SOL
      'EPjFWdd5AufqSSqeM2qN1xzybapC8G4wEGGkZwyTDt1v': 6, // USDC
      'Es9vMFrzaCERmJfrF4H2FYD4KCoNkY11McCe8BenwNYB': 6, // USDT
      'DezXAZ8z7PnrnRJjz3wXBoRgixCa6xjnB7YaB1pPB263': 5, // BONK
      'JUPyiwrYJFskUPiHa7hkeR8VUtAeFoSYbKedZNsDvCN': 6, // JUP
      'mSoLzYCxHdYgdzU16g5QSh3i5K3z3KZK7ytfqcJm7So': 9, // mSOL
      '7dHbWXmci3dT8UFYWYZweBLXgycu7Y3iL6trKn1Y7ARj': 9, // stSOL
    };
    
    if (knownDecimals[tokenMint] !== undefined) {
      this.tokenDecimalsCache.set(tokenMint, knownDecimals[tokenMint]);
      return knownDecimals[tokenMint];
    }

    try {
      const mint = new PublicKey(tokenMint);
      const mintInfo = await this.connection.getParsedAccountInfo(mint, 'processed');
      
      if (mintInfo.value && 'parsed' in mintInfo.value.data) {
        const decimals = mintInfo.value.data.parsed.info.decimals;
        this.tokenDecimalsCache.set(tokenMint, decimals);
        return decimals;
      }
      
      // Default to 9 decimals (SOL standard)
      return 9;
    } catch (error) {
      console.error('Failed to get token decimals, using default:', error);
      return 9;
    }
  }

  /**
   * Get current token price in SOL (cached)
   */
  private priceCache = new Map<string, { price: number; timestamp: number }>();
  private PRICE_CACHE_TTL = 30000; // 30 seconds

  async getTokenPrice(tokenMint: string): Promise<number | null> {
    // Check cache
    const cached = this.priceCache.get(tokenMint);
    if (cached && Date.now() - cached.timestamp < this.PRICE_CACHE_TTL) {
      return cached.price;
    }

    try {
      // Get a small quote to determine price (0.001 SOL)
      const quote = await this.getQuote(
        this.SOL_MINT,
        tokenMint,
        1000000, 
        50
      );

      if (!quote) return null;

      const decimals = await this.getTokenDecimals(tokenMint);
      const inputAmount = parseFloat(quote.inAmount) / LAMPORTS_PER_SOL;
      const outputAmount = parseFloat(quote.outAmount) / Math.pow(10, decimals);
      const price = inputAmount / outputAmount;
      
      // Cache the price
      this.priceCache.set(tokenMint, { price, timestamp: Date.now() });
      
      return price;
    } catch (error) {
      console.error('Failed to get token price:', error);
      return null;
    }
  }

  /**
   * Monitor token price
   */
  monitorTokenPrice(
    tokenMint: string,
    targetPrice: number,
    callback: (currentPrice: number) => void,
    intervalMs: number = 5000
  ): { stop: () => void } {
    let intervalId: NodeJS.Timeout;
    
    const checkPrice = async () => {
      const price = await this.getTokenPrice(tokenMint);
      if (price) {
        callback(price);
        
        if (price >= targetPrice) {
          clearInterval(intervalId);
        }
      }
    };

    // Start monitoring
    intervalId = setInterval(checkPrice, intervalMs);
    
    // Initial check
    checkPrice();

    return {
      stop: () => clearInterval(intervalId)
    };
  }

  /**
   * Get portfolio value in SOL
   */
  async getPortfolioValue(walletAddress: string): Promise<{
    totalValue: number;
    tokens: Array<{
      mint: string;
      balance: number;
      valueInSol: number;
    }>;
  }> {
    try {
      const wallet = new PublicKey(walletAddress);
      
      // Get SOL balance
      const solBalance = await this.connection.getBalance(wallet, 'processed') / LAMPORTS_PER_SOL;
      
      // Get all token accounts
      const tokenAccounts = await this.connection.getParsedTokenAccountsByOwner(wallet, {
        programId: new PublicKey('TokenkegQfeZyiNwAJbNbGKPFXCWuBvf9Ss623VQ5DA')
      }, 'processed');

      const tokens = [];
      let totalTokenValue = 0;

      // Process in parallel for speed
      const promises = tokenAccounts.value.map(async (account) => {
        const tokenInfo = account.account.data.parsed.info;
        const balance = tokenInfo.tokenAmount.uiAmount;
        
        if (balance > 0) {
          const price = await this.getTokenPrice(tokenInfo.mint);
          const valueInSol = price ? balance * price : 0;
          
          return {
            mint: tokenInfo.mint,
            balance,
            valueInSol
          };
        }
        return null;
      });

      const results = await Promise.all(promises);
      
      for (const result of results) {
        if (result) {
          tokens.push(result);
          totalTokenValue += result.valueInSol;
        }
      }

      return {
        totalValue: solBalance + totalTokenValue,
        tokens
      };
    } catch (error) {
      console.error('Failed to get portfolio value:', error);
      return {
        totalValue: 0,
        tokens: []
      };
    }
  }

  /**
   * Validate token mint address
   */
  async validateTokenMint(tokenMint: string): Promise<boolean> {
    try {
      const mint = new PublicKey(tokenMint);
      const mintInfo = await this.connection.getParsedAccountInfo(mint, 'processed');
      
      return mintInfo.value !== null && 'parsed' in mintInfo.value.data;
    } catch (error) {
      return false;
    }
  }
}

// Export factory function
export const tradingService = (connection?: Connection) => new TradingService(connection);